import * as React from 'react';
import { mergeProps, useObjectRef } from 'react-aria';

import { AtomButton, type AtomButtonProps, Button, type ButtonProps } from '@gitbook/spine-react';

import { type UsePaletteTriggerProps, usePaletteTrigger } from './PaletteTrigger';

export type PaletteButtonProps = Omit<ButtonProps, 'onPress'> & Omit<UsePaletteTriggerProps, 'ref'>;

export type PaletteAtomButtonProps = Omit<AtomButtonProps, 'onPress'> &
    Omit<UsePaletteTriggerProps, 'ref'>;

/**
 * Button that opens a palette when pressed.
 */
export function PaletteButton({
    ref: forwardedRef,
    ...props
}: PaletteButtonProps & { ref?: React.ForwardedRef<HTMLButtonElement> }) {
    const { palette, ...otherButtonProps } = props;
    const ref = useObjectRef(forwardedRef);
    const { triggerProps, opened } = usePaletteTrigger({
        ref,
        palette,
        isDisabled: props.disabled,
    });

    return (
        <Button
            onClick={(e) => e.preventDefault()}
            active={opened}
            {...mergeProps(triggerProps, otherButtonProps)}
            className={otherButtonProps.className}
        />
    );
}

/**
 * Low-level button that opens a palette when pressed and renders custom trigger content.
 */
export function PaletteAtomButton({
    ref: forwardedRef,
    ...props
}: PaletteAtomButtonProps & { ref?: React.ForwardedRef<HTMLButtonElement> }) {
    const { palette, ...otherButtonProps } = props;
    const ref = useObjectRef(forwardedRef);
    const { triggerProps } = usePaletteTrigger({
        ref,
        palette,
        isDisabled: props.disabled,
    });

    return (
        <AtomButton
            onClick={(e) => e.preventDefault()}
            {...mergeProps(triggerProps, otherButtonProps)}
            className={otherButtonProps.className}
        />
    );
}
